import { assert } from 'chai'
import CONFIG from '~config/env.js'
import Vue from 'vue'
import { v4 as uuid } from 'uuid'
import {
  getAllVisitResultsQuery,
  getLandVisitsByLandIdQuery,
  updateLandVisitMutation,
  createLandVisitMutation,
  createVisitResultMutation,
  updateVisitResultMutation,
  deleteVisitResultMutation
} from '~graphql/landVisits.graphql'
import {
  createLayerMutation,
  updateLayerMutation,
  deleteLayerMutation,
  getAllLayersTypesQuery,
  getLayersByProjectIdQuery
} from '~graphql/layers.graphql'
import {
  getSpatialRefSysQuery,
  getSpatialRefSysByIdQuery,
  createGeometryMutation,
  updateGeometryMutation,
  importLandGeometryMutation,
  createLandGeometryMutation,
  createLandGeometryWithReturnMutation,
  updateLandGeometryMutation,
  getGeometriesByLayerIdQuery,
  getLandGeometriesByLayerIdQuery,
  getGeometriesPropertiesByIdsQuery,
  getGeometryByIdQuery
} from '~graphql/geometry.graphql'
import {
  getLandByIdQuery,
  getLandKeysByIdsQuery,
  createLandMutation,
  updateLandMutation,
  updateLandNoReturnMutation,
  deleteLandMutation,
  getLandsByProjectIdQuery,
  createLandStatusMutation,
  getLandsQuery
} from '~graphql/land.graphql'
import {
  getResidentsNamesByLandIdQuery,
  createResidentMutation,
  updateResidentMutation,
  deleteResidentMutation,
  getResidentByIdQuery,
  getResidentsQuery
} from '~graphql/resident.graphql'
import _ from 'lodash'

export default {
  getSpatialReferenceSystems({ state, dispatch }, text) {
    return dispatch('getItems', {
      state,
      loader: 'spatialRefSys:get',
      query: getSpatialRefSysQuery,
      variables: {
        text
      },
      resultPath: 'viewSpatialRefSies.nodes'
    })
  },
  getSpatialReferenceSystemById({ state, dispatch }, srid) {
    return dispatch('getItems', {
      state,
      loader: 'spatialRefSys:get',
      query: getSpatialRefSysByIdQuery,
      variables: {
        srid
      },
      resultPath: 'viewSpatialRefSies.nodes'
    })
  },
  getAllVisitResults({ state, dispatch }) {
    return dispatch('getItems', {
      state,
      modelName: 'visitResults',
      loader: 'visitResults:get',
      query: getAllVisitResultsQuery,
      resultPath: 'visitResults.nodes'
    })
  },
  getAllLayerTypes({ state, dispatch }) {
    return dispatch('getItems', {
      state,
      modelName: 'layerTypes',
      loader: 'layerTypes:get',
      query: getAllLayersTypesQuery,
      resultPath: 'layerTypes.nodes'
    })
  },
  getAllLayersByProjectId({ state, dispatch }, projectId) {
    return dispatch('getItems', {
      state,
      modelName: 'layers',
      loader: 'layers:get',
      query: getLayersByProjectIdQuery,
      resultPath: 'layers.nodes',
      variables: {
        projectId
      }
    })
  },
  importShapefile({ dispatch, rootState }, payload) {
    const { layerTypeId, geoJson } = payload
    const firstFeature = geoJson.features[0]
    const columnNames = Object.keys(firstFeature.properties).map(key => {
      return key.toLowerCase()
    })
    const automaticColumns = columnNames.map(key => {
      return {
        component: 'dynamic-text-field',
        label: key,
        model: key,
        showAtList: true,
        showAtSideBar: true,
        format: 'string'
      }
    })

    const projectId = rootState.projectsModule.selectedProject.id

    const layerPayload = {
      id: uuid(),
      name: geoJson.fileName,
      layerTypeId,
      projectId,
      properties: {
        color: CONFIG.DEFAULT_VECTOR_COLOR,
        columns: automaticColumns
      }
    }

    Vue.loadingDialog({ text: 'Iniciando importação' })
    return dispatch('createLayer', layerPayload).then(layer => {
      const geometries = geoJson.features.map(feature => {
        const featureProperties = {}

        _.mapKeys(feature.properties, (value, key) => {
          featureProperties[key.toLowerCase()] = value
        })

        const geometry = {
          id: uuid(),
          layerId: layer.id,
          projectId,
          geometry: feature.geometry,
          properties: featureProperties
        }

        return geometry
      })

      const totalRows = geometries.length

      Vue.loadingDialog({ text: 'Iniciando importação', step: 0, numSteps: totalRows })
      Vue.bus.$on('geometry-create:progress', ({ remainingItems }) => {
        const step = totalRows - remainingItems
        Vue.loadingDialog({ text: `Importanto ${step} de ${totalRows}`, step, numSteps: totalRows })
      })

      Vue.bus.$on('geometry-create:finished', () => {
        Vue.toasted.success('Importação finalizada com sucesso!')
        Vue.closeLoadingDialog()
        dispatch('getGeometriesByLayerId', { layerId: layer.id })
      })

      dispatch('createGeometries', geometries)
    })
  },
  importLandGeometries({ state, dispatch }, geometries) {
    return dispatch('createItems', {
      items: geometries,
      state,
      loader: 'geometry-create',
      mutation: importLandGeometryMutation,
      resultPath: 'createGeometry.geometry',
      progressEvent: 'geometry-create:progress'
    })
  },
  createLandGeometries({ state, dispatch }, geometries) {
    return dispatch('createItems', {
      items: geometries,
      state,
      loader: 'geometry-create',
      mutation: createLandGeometryMutation,
      resultPath: 'createGeometry.geometry',
      progressEvent: 'geometry-create:progress'
    })
  },
  createLandGeometry({ state, dispatch }, geometries) {
    return dispatch('createItem', {
      item: geometries,
      state,
      loader: 'geometry-create',
      addTo: ['landGeometries'],
      mutation: createLandGeometryWithReturnMutation,
      resultPath: 'createGeometry.geometry',
      progressEvent: 'geometry-create:progress'
    })
  },
  updateLandGeometry({ state, dispatch }, payload) {
    return dispatch('updateItem', {
      item: payload,
      state,
      addTo: ['landGeometries'],
      loader: 'geometry-update',
      mutation: updateLandGeometryMutation,
      resultPath: 'updateGeometry.geometry'
    })
  },
  createGeometry({ state, dispatch }, geometry) {
    return dispatch('createItem', {
      item: geometry,
      state,
      loader: 'geometry-create',
      mutation: createGeometryMutation,
      resultPath: 'createGeometry.geometry'
    })
  },
  createGeometries({ state, dispatch }, geometries) {
    return dispatch('createItems', {
      items: geometries,
      state,
      loader: 'geometry-create',
      mutation: createGeometryMutation,
      resultPath: 'createGeometry.geometry',
      progressEvent: 'geometry-create:progress'
    })
  },
  updateGeometries({ state, dispatch }, geometries) {
    return dispatch('updateItems', {
      items: geometries,
      state,
      loader: 'geometry-update',
      mutation: updateGeometryMutation,
      resultPath: 'updateGeometry.geometry',
      progressEvent: 'geometry-update:progress'
    })
  },
  updateGeometry({ state, dispatch }, geometries) {
    return dispatch('updateItem', {
      item: geometries,
      state,
      loader: 'geometry-update',
      mutation: updateGeometryMutation,
      resultPath: 'updateGeometry.geometry'
    })
  },
  setSelectedLayers({ state, commit }, selectedLayers) {
    commit('SET_ITEM', {
      state,
      item: selectedLayers,
      modelName: 'selectedLayers'
    })
  },
  clearSelectedLayer({ state, commit }) {
    commit('SET_ITEM', {
      state,
      item: {},
      modelName: 'selectedLayer'
    })
  },
  clearLayers({ state, commit }) {
    commit('SET_ITEM', {
      state,
      item: [],
      modelName: 'layers'
    })
    commit('SET_ITEM', {
      state,
      item: {},
      modelName: 'selectedLayer'
    })
  },
  deleteLayer({ state, dispatch }, id) {
    return dispatch('deleteItem', {
      id,
      state,
      modelName: 'selectedLayer',
      removeFrom: ['layers'],
      loader: 'layer:delete',
      mutation: deleteLayerMutation
    })
  },
  updateLayer({ state, dispatch }, table) {
    return dispatch('updateItem', {
      item: table,
      state,
      modelName: 'selectedLayer',
      addTo: ['layers'],
      loader: 'layer:update',
      mutation: updateLayerMutation,
      resultPath: 'updateLayer.layer'
    })
  },
  createLayer({ state, dispatch }, layer) {
    return dispatch('createItem', {
      item: layer,
      state,
      addTo: ['layers'],
      loader: 'layer:create',
      mutation: createLayerMutation,
      resultPath: 'createLayer.layer'
    })
  },
  getLandGeometriesByLayerId({ state, dispatch }, payload) {
    assert.exists(payload.layerId)

    return dispatch('getItems', {
      state,
      loader: 'geometries:get',
      modelName: 'landGeometries',
      query: getLandGeometriesByLayerIdQuery,
      variables: {
        layerId: payload.layerId
      },
      resultPath: 'geometries.nodes'
    })
  },

  getGeometriesByLayerId({ state, dispatch, commit }, payload) {
    assert.exists(payload.layerId)

    const { layerId } = payload

    return dispatch('getItems', {
      state,
      loader: 'geometries:get',
      query: getGeometriesByLayerIdQuery,
      variables: {
        layerId: payload.layerId
      },
      resultPath: 'geometries.nodes'
    }).then(geometries => {
      const newOtherGeometries = {
        ...state.otherGeometries,
        [layerId]: geometries
      }
      commit('SET_ITEM', {
        state,
        item: newOtherGeometries,
        modelName: 'otherGeometries'
      })
      return geometries
    })
  },

  getGeometriesPropertiesByIds({ state, dispatch }, ids) {
    return dispatch('getItems', {
      state,
      loader: 'geometriesProperties:get',
      query: getGeometriesPropertiesByIdsQuery,
      variables: {
        ids
      },
      resultPath: 'geometries.nodes'
    })
  },

  getGeometryById({ state, dispatch }, id) {
    return dispatch('getItemById', {
      id,
      state,
      loader: 'geometry:get',
      query: getGeometryByIdQuery,
      resultPath: 'geometry',
      useCache: false
    })
  },

  clearGeometries({ state, commit }) {
    commit('SET_ITEM', {
      state,
      item: [],
      modelName: 'landGeometries'
    })

    commit('SET_ITEM', {
      state,
      item: {},
      modelName: 'otherGeometries'
    })
  },

  getLands({ state, dispatch }, { filter, offset, first, orderBy }) {
    const defaultFilter = {
      id: {
        isNull: false
      } // used to allow empty filter
    }
    const mergedFilter = _.merge(filter, defaultFilter)

    return dispatch('getItems', {
      state,
      loader: 'lands:get',
      modelName: 'lands',
      query: getLandsQuery,
      resultPath: 'lands.nodes',
      totalPath: 'lands.totalCount',
      totalModelName: 'landsTotalCount',
      variables: {
        filter: mergedFilter,
        offset,
        first,
        orderBy
      }
    })
  },

  getLandsByProjectId({ state, dispatch }, id) {
    return dispatch('getItems', {
      state,
      loader: 'lands:get',
      modelName: 'lands',
      query: getLandsByProjectIdQuery,
      variables: {
        projectId: id
      },
      resultPath: 'lands.nodes',
      useCache: false
    })
  },

  clearLands({ state, commit }) {
    commit('SET_ITEM', {
      state,
      item: [],
      modelName: 'lands'
    })
  },

  getLandById({ state, dispatch }, id) {
    return dispatch('getItemById', {
      id,
      state,
      modelName: 'selectedLand',
      loader: 'land:get',
      query: getLandByIdQuery,
      resultPath: 'land',
      useCache: false
    })
  },
  getLandKeysByIds({ state, dispatch }, ids) {
    return dispatch('getItems', {
      state,
      loader: 'landKeys:get',
      query: getLandKeysByIdsQuery,
      resultPath: 'lands.nodes',
      variables: {
        ids
      },
      useCache: false
    })
  },

  deleteLand({ state, dispatch }, id) {
    return dispatch('deleteItem', {
      id,
      state,
      modelName: 'selectedLand',
      removeFrom: ['lands'],
      loader: 'land:delete',
      mutation: deleteLandMutation
    })
  },
  updateLand({ state, dispatch }, land) {
    return dispatch('updateItem', {
      item: land,
      state,
      modelName: 'selectedLand',
      addTo: ['lands'],
      loader: 'land:update',
      mutation: updateLandMutation,
      resultPath: 'updateLand.land'
    }).then(land => {
      if (state.landGeometries) {
        state.landGeometries.forEach(geometry => {
          if (geometry?.land?.id === land?.id) {
            geometry.land = land
          }
        })
      }

      return land
    })
  },
  updateLands({ state, dispatch }, lands) {
    return dispatch('updateItems', {
      items: lands,
      state,
      loader: 'land-update',
      mutation: updateLandNoReturnMutation,
      resultPath: 'updateLand.land',
      progressEvent: 'land-update:progress'
    })
  },
  createLand({ state, dispatch }, land) {
    return dispatch('createItem', {
      item: land,
      state,
      addTo: ['lands'],
      loader: 'land:create',
      mutation: createLandMutation,
      resultPath: 'createLand.land'
    })
  },
  createLands({ state, dispatch }, lands) {
    return dispatch('createItems', {
      items: lands,
      state,
      loader: 'land-create',
      mutation: createLandMutation,
      resultPath: 'createLand.land',
      progressEvent: 'land-create:progress'
    })
  },

  createLandStatus({ state, dispatch }, landStatus) {
    return dispatch('createItem', {
      item: { landStatus },
      state,
      loader: 'land-status:create',
      mutation: createLandStatusMutation,
      resultPath: 'createLandStatus.landStatus'
    })
  },

  getResidents({ state, dispatch }, { filter, offset, first, orderBy }) {
    const defaultFilter = {
      id: {
        isNull: false
      } // used to allow empty filter
    }
    const mergedFilter = _.merge(filter, defaultFilter)

    return dispatch('getItems', {
      state,
      modelName: 'residents',
      loader: 'residents:get',
      query: getResidentsQuery,
      variables: {
        filter: mergedFilter,
        offset,
        first,
        orderBy
      },
      resultPath: 'residents.nodes',
      totalPath: 'residents.totalCount',
      totalModelName: 'residentsTotalCount',
      useCache: false
    })
  },

  getResidentById({ state, dispatch }, id) {
    return dispatch('getItemById', {
      id,
      state,
      modelName: 'selectedResident',
      loader: 'resident:get',
      query: getResidentByIdQuery,
      resultPath: 'resident',
      useCache: false
    })
  },

  clearResidents({ state, commit }) {
    commit('SET_ITEM', {
      state,
      item: [],
      modelName: 'residents'
    })
  },

  getResidentsByLandId({ state, dispatch }, id) {
    const filter = {
      landId: {
        equalTo: id
      }
    }

    return dispatch('getItems', {
      state,
      modelName: 'landResidents',
      loader: 'landResidents:get',
      query: getResidentsQuery,
      variables: {
        filter
      },
      resultPath: 'residents.nodes',
      useCache: false
    })
  },

  getResidentsNamesByLandId({ state, dispatch }, id) {
    return dispatch('getItems', {
      state,
      loader: 'landResidents:get',
      query: getResidentsNamesByLandIdQuery,
      variables: {
        landId: id
      },
      resultPath: 'residents.nodes',
      useCache: false
    })
  },
  clearLandResidents({ state, commit }) {
    commit('SET_ITEM', {
      state,
      item: [],
      modelName: 'landResidents'
    })
  },
  deleteResident({ state, dispatch }, payload) {
    return dispatch('updateItem', {
      item: payload,
      state,
      modelName: 'selectedResident',
      removeFrom: ['residents', 'landResidents'],
      loader: 'resident:delete',
      mutation: deleteResidentMutation,
      resultPath: 'updateResident.resident'
    })
  },
  updateResident({ state, dispatch }, table) {
    return dispatch('updateItem', {
      item: table,
      state,
      modelName: 'selectedResident',
      addTo: ['residents', 'landResidents'],
      loader: 'resident:update',
      mutation: updateResidentMutation,
      resultPath: 'updateResident.resident'
    })
  },
  createResident({ state, dispatch }, resident) {
    return dispatch('createItem', {
      item: resident,
      state,
      addTo: ['residents', 'landResidents'],
      loader: 'resident:create',
      mutation: createResidentMutation,
      resultPath: 'createResident.resident'
    })
  },
  getVisitsByLandId({ state, dispatch }, id) {
    return dispatch('getItems', {
      state,
      modelName: 'landVisits',
      loader: 'landVisits:get',
      query: getLandVisitsByLandIdQuery,
      variables: {
        landId: id
      },
      resultPath: 'landVisits.nodes',
      useCache: false
    })
  },
  updateLandVisit({ state, dispatch }, payload) {
    return dispatch('updateItem', {
      item: payload,
      state,
      addTo: ['landVisits'],
      loader: 'resident:update',
      mutation: updateLandVisitMutation,
      resultPath: 'updateLandVisit.landVisit'
    })
  },
  createLandVisit({ state, dispatch }, payload) {
    return dispatch('createItem', {
      item: payload,
      state,
      addTo: ['landVisits'],
      loader: 'landVisit:create',
      mutation: createLandVisitMutation,
      resultPath: 'createLandVisit.landVisit'
    })
  },

  // CRUD
  // Visit Result
  createVisitResult({ state, dispatch }, visitResult) {
    return dispatch('createItem', {
      item: { visitResult },
      state,
      addTo: ['visitResults'],
      loader: 'visitResult:create',
      mutation: createVisitResultMutation,
      resultPath: 'createVisitResult.visitResult'
    })
  },
  updateVisitResult({ state, dispatch }, visitResult) {
    return dispatch('createItem', {
      item: visitResult,
      state,
      addTo: ['visitResults'],
      loader: 'visitResult:update',
      mutation: updateVisitResultMutation,
      resultPath: 'updateVisitResult.visitResult'
    })
  },
  deleteVisitResult({ state, dispatch }, id) {
    return dispatch('deleteItem', {
      id,
      state,
      removeFrom: ['visitResults'],
      loader: 'visitResult:delete',
      mutation: deleteVisitResultMutation
    })
  }
}
